import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import Avatar from '../../../base/avatar/components/Avatar';
import Icon from '../../../base/icons/components/Icon';
import { IconMic, IconMicSlash, IconUser } from '../../../base/icons/svg';
import VideoTrack from '../../../base/media/components/web/VideoTrack';
import { getParticipantById, getParticipantDisplayName } from '../../../base/participants/functions';
import { getVideoTrackByParticipant } from '../../../base/tracks/functions';
import { isParticipantAudioMuted } from '../../../base/tracks/functions.any';
import { MOBILE_SCREENSHARE_PIP_AVATAR_SIZE, MOBILE_SCREENSHARE_PIP_BORDER_RADIUS, MOBILE_SCREENSHARE_PIP_HEIGHT, MOBILE_SCREENSHARE_PIP_OFFSET, MOBILE_SCREENSHARE_PIP_WIDTH } from '../../constants';
import { getMobileScreensharePresenterId } from '../../functions.web';
/**
 * 共享者小窗内 video 元素样式（避免 JSX 内联 style 触发 eslint）。
 */
const PRESENTER_PIP_VIDEO_STYLE = {
    height: '100%',
    objectFit: 'cover',
    width: '100%'
};
const useStyles = makeStyles()(theme => {
    return {
        pip: {
            backgroundColor: theme.palette.thumbnailBackground,
            borderRadius: MOBILE_SCREENSHARE_PIP_BORDER_RADIUS,
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.35)',
            height: MOBILE_SCREENSHARE_PIP_HEIGHT,
            overflow: 'hidden',
            pointerEvents: 'auto',
            position: 'absolute',
            right: MOBILE_SCREENSHARE_PIP_OFFSET,
            top: MOBILE_SCREENSHARE_PIP_OFFSET,
            width: MOBILE_SCREENSHARE_PIP_WIDTH,
            zIndex: 1
        },
        media: {
            alignItems: 'center',
            display: 'flex',
            height: '100%',
            justifyContent: 'center',
            position: 'relative',
            width: '100%'
        },
        video: {
            height: '100%',
            objectFit: 'cover',
            width: '100%'
        },
        label: {
            alignItems: 'center',
            background: 'linear-gradient(transparent, rgba(0, 0, 0, 0.72))',
            bottom: 0,
            color: theme.palette.text01,
            display: 'flex',
            fontSize: '0.6875rem',
            gap: 4,
            left: 0,
            lineHeight: 1.2,
            padding: '16px 6px 6px',
            position: 'absolute',
            right: 0
        },
        name: {
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        }
    };
});
/**
 * 手机共享聚焦布局：右上角仅展示当前共享者摄像头小窗。
 *
 * @returns {ReactElement|null}
 */
function MobileScreensharePresenterPip() {
    const { t } = useTranslation();
    const { classes } = useStyles();
    const presenterId = useSelector(getMobileScreensharePresenterId);
    const participant = useSelector((state) => (presenterId ? getParticipantById(state, presenterId) : undefined));
    const displayName = useSelector((state) => (presenterId ? getParticipantDisplayName(state, presenterId) : ''));
    const videoTrack = useSelector((state) => getVideoTrackByParticipant(state, participant));
    const audioMuted = useSelector((state) => isParticipantAudioMuted(participant, state));
    if (!presenterId || !participant) {
        return null;
    }
    const hasCameraVideo = Boolean(videoTrack && !videoTrack.muted && videoTrack.videoType !== 'desktop');
    return (React.createElement("div", { "aria-label": t('meetingUi.mobileScreenshare.presenterPipAria', { name: displayName }), className: classes.pip, role: 'group' },
        React.createElement("div", { className: classes.media },
            hasCameraVideo ? (React.createElement(VideoTrack, { className: classes.video, id: `mobileScreensharePip_${presenterId}`, muted: true, style: PRESENTER_PIP_VIDEO_STYLE, videoTrack: videoTrack })) : (React.createElement(Avatar, { participantId: presenterId, size: MOBILE_SCREENSHARE_PIP_AVATAR_SIZE })),
            React.createElement("div", { className: classes.label },
                React.createElement(Icon, { size: 12, src: IconUser }),
                React.createElement(Icon, { size: 12, src: audioMuted ? IconMicSlash : IconMic }),
                React.createElement("span", { className: classes.name }, displayName)))));
}
export default MobileScreensharePresenterPip;
